import axios from 'axios';

// Update API URL to use port 3000 instead of 5000
const API_URL = `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}`;

// Create a dedicated axios instance for dashboard requests
const dashboardAxios = axios.create({
  baseURL: API_URL,
  timeout: 10000, // 10 seconds timeout
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to log requests
dashboardAxios.interceptors.request.use(
  config => {
    console.log('📡 Dashboard API Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL
    });
    return config;
  },
  error => {
    console.error('❌ Dashboard API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to log responses
dashboardAxios.interceptors.response.use(
  response => {
    console.log('✅ Dashboard API Response:', {
      status: response.status,
      url: response.config.url,
      dataShape: typeof response.data
    });
    return response;
  },
  error => {
    console.error('❌ Dashboard API Response Error:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      data: error.response?.data
    });
    return Promise.reject(error);
  }
);

/**
 * Dashboard Service
 * 
 * Handles all API calls related to dashboard data
 * Includes error handling and response formatting
 */
const dashboardService = {
  /**
   * Get authentication header with JWT token
   * @returns {Object} Headers object with Authorization
   */
  getAuthHeader() {
    const token = localStorage.getItem('token');
    console.log('🔑 Dashboard Service: Using token for auth:', token ? 'Token found' : 'No token!');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  },

  /**
   * Extract numeric value from potentially nested object or direct value
   * @param {*} value - The value to extract number from
   * @returns {number} The extracted number or 0
   */
  extractNumericValue(value) {
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string') {
      return parseFloat(value) || 0;
    }
    if (value && typeof value === 'object') {
      // If it's an object, try to find a numeric value in common properties
      const possibleValues = [
        value.amount,
        value.value,
        value.total,
        value.balance
      ];
      for (const val of possibleValues) {
        if (val !== undefined) {
          return this.extractNumericValue(val);
        }
      }
    }
    return 0;
  },

  /**
   * Fetch all dashboard data
   * @param {AbortSignal} signal - AbortController signal for cancellation
   * @param {Object} dateRange - Date range filter { startDate, endDate }
   * @param {string} dataView - View filter: 'personal', 'partner', or 'combined'
   * @returns {Promise} Dashboard data including totals, trends, and recent activity
   */
  async getDashboardData(signal, dateRange = null, dataView = 'combined') {
    try {
      console.log('📊 Dashboard Service: Fetching dashboard data...', { dateRange, dataView });
      console.log('📊 Dashboard Service: API URL is set to:', API_URL);
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('🔒 Dashboard Service: No token found! Cannot make authenticated request.');
        throw new Error('Authentication token is missing. Please log in again.');
      }

      // Check if user is demo account
      let isDemoUser = false;
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        isDemoUser = parsedUser?.email === 'demo@thewallet.com';
      }
      
      // Build query params with date range and dataView
      const params = {};
      if (dateRange && dateRange.startDate && dateRange.endDate) {
        params.startDate = dateRange.startDate.toISOString().split('T')[0];
        params.endDate = dateRange.endDate.toISOString().split('T')[0];
        console.log('📊 Dashboard Service: Adding date range params:', params);
      }
      
      // Add dataView parameter if provided
      if (dataView) {
        params.dataView = dataView;
        console.log('📊 Dashboard Service: Adding dataView param:', dataView);
      }
      
      const config = {
        ...this.getAuthHeader(),
        signal, // Add the abort signal to the request config
        params // Add query parameters
      };
      
      const dashboardEndpoint = `api/dashboard/overview`;
      console.log('📊 Dashboard Service: Making API request to:', `${API_URL}/${dashboardEndpoint}`, 'with params:', params);
      
      try {
        // Check if request was aborted before sending
        if (signal?.aborted) {
          console.log('🛑 Dashboard Service: Request was already aborted, skipping API call');
          throw new DOMException('The operation was aborted', 'AbortError');
        }
        
        const response = await dashboardAxios.get(dashboardEndpoint, config);
        
        // Check if request was aborted before processing response
        if (signal?.aborted) {
          console.log('🛑 Dashboard Service: Request was aborted, discarding response');
          throw new DOMException('The operation was aborted', 'AbortError');
        }

        console.log('📦 Dashboard Service: Raw dashboard response:', response.data);
        
        // Debug logging for specific fields
        console.log('📊 Dashboard Service: totalBalance:', response.data.totalBalance);
        console.log('📊 Dashboard Service: monthlyIncome:', response.data.monthlyIncome);
        console.log('📊 Dashboard Service: monthlyExpenses:', response.data.monthlyExpenses);
        console.log('📊 Dashboard Service: transactionTrends:', response.data.transactionTrends);
        console.log('📊 Dashboard Service: categoryBreakdown:', response.data.categoryBreakdown);

        // Extract data from response
        let dashboardData = {};
        
        if (response.data?.success && response.data?.data) {
          // If response follows { success: true, data: { ... } } format
          dashboardData = response.data.data;
          console.log('📊 Dashboard Service: Using data from response.data.data');
        } else if (typeof response.data === 'object') {
          // If response is direct data object
          dashboardData = response.data;
          console.log('📊 Dashboard Service: Using data directly from response.data');
        }

        console.log('🔍 Dashboard Service: Processing dashboard data:', dashboardData);

        // Get transaction trends data directly from the response
        // Don't attempt to fetch it separately if it already exists
        let transactionTrendsData = dashboardData.transactionTrends || [];
        console.log('📊 Dashboard Service: Initial transaction trends data:', 
          Array.isArray(transactionTrendsData) ? transactionTrendsData.length : 'not an array');
        
        // Only try to fetch transaction trends separately if array is missing entirely
        if (!transactionTrendsData || !Array.isArray(transactionTrendsData)) {
          console.log('📊 Dashboard Service: Transaction trends not found or invalid in response, fetching separately');
          
          try {
            const trendsResponse = await dashboardAxios.get('transactions/trends', config);
            console.log('📊 Dashboard Service: Trends response:', trendsResponse.data);
            
            // Check for the transactionTrends array in the response
            if (trendsResponse.data && 
                trendsResponse.data.transactionTrends && 
                Array.isArray(trendsResponse.data.transactionTrends)) {
              transactionTrendsData = trendsResponse.data.transactionTrends;
              console.log('📊 Dashboard Service: Got transaction trends separately:', transactionTrendsData.length);
            } else {
              // Use empty array if data is missing or invalid
              transactionTrendsData = [];
              console.log('📊 Dashboard Service: No valid transaction trends received from separate endpoint');
            }
          } catch (trendsError) {
            console.error('❌ Dashboard Service: Failed to fetch transaction trends:', trendsError);
            // Use empty array for trends data
            transactionTrendsData = [];
          }
        }

        // Get category breakdown data directly from the response
        // Don't attempt to derive it if it already exists
        let categoryBreakdownData = dashboardData.categoryBreakdown || [];
        console.log('📊 Dashboard Service: Initial category breakdown data:', 
          Array.isArray(categoryBreakdownData) ? categoryBreakdownData.length : 'not an array');
        
        // If missing, try to derive from transaction trends as a fallback
        if (!categoryBreakdownData || !Array.isArray(categoryBreakdownData)) {
          console.log('📊 Dashboard Service: Category breakdown not found in response');
          
          if (transactionTrendsData && transactionTrendsData.length > 0) {
            console.log('📊 Dashboard Service: Using transaction trends for category breakdown');
            categoryBreakdownData = transactionTrendsData.map(trend => ({
              category: trend.category,
              amount: trend.amount,
              percentage: trend.percentage
            }));
          } else {
            // Use empty array if no trends data available
            categoryBreakdownData = [];
            console.log('📊 Dashboard Service: No data available for category breakdown');
          }
        }

        // Normalize the data
        const normalizedData = {
          // Keep the original structure for these objects
          totalBalance: dashboardData.totalBalance || { amount: 0, percentageChange: 0 },
          monthlyIncome: dashboardData.monthlyIncome || { amount: 0, percentageChange: 0 },
          monthlyExpenses: dashboardData.monthlyExpenses || { amount: 0, percentageChange: 0 },
          spendingTrends: Array.isArray(dashboardData.spendingTrends) 
            ? dashboardData.spendingTrends.map(trend => ({
                ...trend,
                amount: this.extractNumericValue(trend.amount)
              }))
            : [],
          categoryBreakdown: Array.isArray(categoryBreakdownData)
            ? categoryBreakdownData.map(category => ({
                ...category,
                amount: this.extractNumericValue(category.amount)
              }))
            : [],
          transactionTrends: Array.isArray(transactionTrendsData)
            ? transactionTrendsData.map(trend => ({
                ...trend,
                amount: this.extractNumericValue(trend.amount)
              }))
            : [],
          // Add the new trend data fields
          incomeTrends: Array.isArray(dashboardData.incomeTrends)
            ? dashboardData.incomeTrends.map(trend => ({
                ...trend,
                amount: this.extractNumericValue(trend.amount)
              }))
            : [],
          savingsTrends: Array.isArray(dashboardData.savingsTrends)
            ? dashboardData.savingsTrends.map(trend => ({
                ...trend,
                amount: this.extractNumericValue(trend.amount)
              }))
            : [],
          debtTrends: Array.isArray(dashboardData.debtTrends)
            ? dashboardData.debtTrends.map(trend => ({
                ...trend,
                amount: this.extractNumericValue(trend.amount)
              }))
            : [],
          recentActivity: Array.isArray(dashboardData.recentActivity)
            ? dashboardData.recentActivity.map(activity => ({
                ...activity,
                amount: this.extractNumericValue(activity.amount)
              }))
            : [],
          isDemo: isDemoUser || (dashboardData.isDemo || false),
          // Preserve record counts for onboarding checklist
          incomeCount: dashboardData.incomeCount ?? 0,
          accountCount: dashboardData.accountCount ?? 0
        };

        console.log('✅ Dashboard Service: Normalized data structure:', Object.keys(normalizedData));
        console.log('✅ Dashboard Service: totalBalance after normalization:', normalizedData.totalBalance);
        console.log('✅ Dashboard Service: monthlyIncome after normalization:', normalizedData.monthlyIncome);
        console.log('✅ Dashboard Service: monthlyExpenses after normalization:', normalizedData.monthlyExpenses);
        console.log('✅ Dashboard Service: transactionTrends count:', normalizedData.transactionTrends.length);
        console.log('✅ Dashboard Service: categoryBreakdown count:', normalizedData.categoryBreakdown.length);
        console.log('✅ Dashboard Service: incomeTrends count:', normalizedData.incomeTrends.length);
        console.log('✅ Dashboard Service: savingsTrends count:', normalizedData.savingsTrends.length);
        console.log('✅ Dashboard Service: debtTrends count:', normalizedData.debtTrends.length);
        console.log('✅ Dashboard Service: incomeCount:', normalizedData.incomeCount);
        console.log('✅ Dashboard Service: accountCount:', normalizedData.accountCount);
        
        return normalizedData;
      } catch (apiError) {
        // Early exit for abort errors
        if (apiError.name === 'CanceledError' || apiError.name === 'AbortError' || 
            (apiError.message && apiError.message.includes('canceled'))) {
          console.log('🛑 Dashboard Service: Request was aborted, breaking error chain');
          throw apiError;
        }
        
        console.error('🔥 Dashboard Service: API request failed:', apiError.message);
        if (apiError.response) {
          console.error('🔥 Dashboard Service: API response status:', apiError.response.status);
          console.error('🔥 Dashboard Service: API response data:', apiError.response.data);
        } else {
          console.error('🔥 Dashboard Service: No response from server. Network error or server unavailable.');
        }
        
        // Propagate the error up to be handled by the component
        throw apiError;
      }
    } catch (error) {
      // Don't log errors for aborted requests
      if (error.name === 'CanceledError' || error.name === 'AbortError' || 
          (error.message && error.message.includes('canceled'))) {
        console.log('🛑 Dashboard Service: Dashboard data fetch aborted');
        throw error;
      }
      console.error('❌ Dashboard Service: Error fetching dashboard data:', error);
      throw error;
    }
  },

  /**
   * Generate offline demo data without making API calls
   * @returns {Object} Demo dashboard data
   */
  getDemoDataOffline() {
    // Current date for reference
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Demo spending trends - last 6 months
    const spendingTrends = [];
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(currentYear, currentMonth - i, 1);
      const monthName = monthDate.toLocaleString('default', { month: 'short' });
      const yearName = monthDate.getFullYear();
      spendingTrends.push({
        month: monthName,
        year: yearName,
        amount: 2500 + Math.round(Math.random() * 1000),
        date: new Date(yearName, monthDate.getMonth(), 1)
      });
    }

    // Demo category breakdown
    const categoryBreakdown = [
      { category: 'Housing', amount: 1200, percentage: 35 },
      { category: 'Food', amount: 500, percentage: 15 },
      { category: 'Transportation', amount: 400, percentage: 12 },
      { category: 'Entertainment', amount: 300, percentage: 9 },
      { category: 'Utilities', amount: 350, percentage: 10 },
      { category: 'Healthcare', amount: 250, percentage: 7 },
      { category: 'Other', amount: 400, percentage: 12 }
    ];

    // Demo transaction trends
    const transactionTrends = [
      { category: 'Bills', amount: 1850, percentage: 53 },
      { category: 'Shopping', amount: 725, percentage: 21 },
      { category: 'Groceries', amount: 525, percentage: 15 },
      { category: 'Entertainment', amount: 350, percentage: 11 }
    ];

    // Demo recent activity
    const recentActivity = [
      {
        _id: 'demo1',
        date: new Date(currentYear, currentMonth, now.getDate() - 1),
        description: 'Grocery Store',
        amount: -127.35,
        category: 'Food',
        type: 'expense'
      },
      {
        _id: 'demo2',
        date: new Date(currentYear, currentMonth, now.getDate() - 2),
        description: 'Monthly Salary',
        amount: 3650.00,
        category: 'Income',
        type: 'income'
      },
      {
        _id: 'demo3',
        date: new Date(currentYear, currentMonth, now.getDate() - 3),
        description: 'Electric Bill',
        amount: -95.20,
        category: 'Utilities',
        type: 'expense'
      },
      {
        _id: 'demo4',
        date: new Date(currentYear, currentMonth, now.getDate() - 5),
        description: 'Restaurant',
        amount: -78.50,
        category: 'Dining',
        type: 'expense'
      },
      {
        _id: 'demo5',
        date: new Date(currentYear, currentMonth, now.getDate() - 7),
        description: 'Gas Station',
        amount: -45.75,
        category: 'Transportation',
        type: 'expense'
      }
    ];

    return {
      totalBalance: { amount: 12750.25, percentageChange: 5.3 },
      monthlyIncome: { amount: 5350.00, percentageChange: 0 },
      monthlyExpenses: { amount: 3450.75, percentageChange: -3.2 },
      spendingTrends,
      categoryBreakdown,
      transactionTrends,
      recentActivity,
      isDemo: true
    };
  },

  /**
   * Force using demo data
   * @returns {Promise} Demo dashboard data 
   */
  async getDemoData() {
    try {
      console.log('🎭 Dashboard Service: Requesting demo data...');
      
      // Check for the demo user email from localStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        const userEmail = parsedUser?.email;
        
        // Only allow demo data for the demo user account
        if (userEmail !== 'demo@thewallet.com') {
          console.log('🔒 Dashboard Service: Demo data restricted - current user is not demo account');
          throw new Error('Demo data is only available for the demo account');
        }
      }
      
      console.log('🎭 Dashboard Service: API URL is set to:', API_URL);
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('🔒 Dashboard Service: No token found for demo data! Using offline demo data.');
        return this.getDemoDataOffline();
      }
      
      const config = this.getAuthHeader();
      const demoEndpoint = `dashboard/demo`;
      
      console.log('🎭 Dashboard Service: Making demo request to:', `${API_URL}/${demoEndpoint}`);
      try {
        const response = await dashboardAxios.get(demoEndpoint, config);
        console.log('🎭 Dashboard Service: Demo data response:', response.data);
        
        // Process the demo data through the same normalization
        const normalizedData = {
          totalBalance: response.data.totalBalance || { amount: 0, percentageChange: 0 },
          monthlyIncome: response.data.monthlyIncome || { amount: 0, percentageChange: 0 },
          monthlyExpenses: response.data.monthlyExpenses || { amount: 0, percentageChange: 0 },
          spendingTrends: Array.isArray(response.data.spendingTrends) 
            ? response.data.spendingTrends.map(trend => ({
                ...trend,
                amount: this.extractNumericValue(trend.amount)
              }))
            : [],
          categoryBreakdown: Array.isArray(response.data.categoryBreakdown)
            ? response.data.categoryBreakdown.map(category => ({
                ...category,
                amount: this.extractNumericValue(category.amount)
              }))
            : [],
          transactionTrends: Array.isArray(response.data.transactionTrends)
            ? response.data.transactionTrends.map(trend => ({
                ...trend,
                amount: this.extractNumericValue(trend.amount)
              }))
            : [],
          recentActivity: Array.isArray(response.data.recentActivity)
            ? response.data.recentActivity.map(activity => ({
                ...activity,
                amount: this.extractNumericValue(activity.amount)
              }))
            : [],
          isDemo: true
        };
        
        console.log('✅ Dashboard Service: Demo data normalized:', normalizedData);
        return normalizedData;
      } catch (apiError) {
        console.error('🔥 Dashboard Service: Demo API request failed:', apiError.message);
        
        // Only fall back to offline demo data for the demo user
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser?.email === 'demo@thewallet.com') {
            console.log('🎭 Dashboard Service: Falling back to offline demo data for demo user');
            return this.getDemoDataOffline();
          }
        }
        
        throw apiError;
      }
    } catch (error) {
      console.error('❌ Dashboard Service: Error fetching demo data:', error);
      
      // Only fall back to offline demo data for the demo user
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser?.email === 'demo@thewallet.com') {
          console.log('🎭 Dashboard Service: Falling back to offline demo data for demo user');
          return this.getDemoDataOffline();
        }
      }
      
      throw error;
    }
  },

  /**
   * Fetch spending trends data
   * @param {string} period - Time period for trends (e.g., '6m', '1y')
   * @param {AbortSignal} signal - AbortController signal for cancellation
   * @returns {Promise<Array>} Spending trend data
   */
  async getSpendingTrends(period = '6m', signal) {
    try {
      console.log(`📊 Fetching spending trends for period: ${period}`);
      const config = {
        ...this.getAuthHeader(),
        signal,
        params: { period }
      };
      
      const response = await dashboardAxios.get(`spending-trends`, config);
      
      // Normalize the data
      let trendsData = [];
      if (response.data?.success && Array.isArray(response.data?.data)) {
        trendsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        trendsData = response.data;
      }

      const normalizedTrends = trendsData.map(trend => ({
        ...trend,
        amount: this.extractNumericValue(trend.amount)
      }));

      console.log('✅ Normalized spending trends:', normalizedTrends);
      return normalizedTrends;
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        console.log('🛑 Spending trends fetch aborted');
        throw error;
      }
      console.error('❌ Error fetching spending trends:', error);
      throw error;
    }
  },

  /**
   * Fetch category breakdown data
   * @param {string} period - Time period for breakdown (e.g., '1m', '3m')
   * @param {AbortSignal} signal - AbortController signal for cancellation
   * @returns {Promise<Array>} Category breakdown data
   */
  async getCategoryBreakdown(period = '1m', signal) {
    try {
      console.log(`🎯 Fetching category breakdown for period: ${period}`);
      const config = {
        ...this.getAuthHeader(),
        signal,
        params: { period }
      };
      
      const response = await dashboardAxios.get(`category-breakdown`, config);
      
      // Normalize the data
      let categoryData = [];
      if (response.data?.success && Array.isArray(response.data?.data)) {
        categoryData = response.data.data;
      } else if (Array.isArray(response.data)) {
        categoryData = response.data;
      }

      const normalizedCategories = categoryData.map(category => ({
        ...category,
        amount: this.extractNumericValue(category.amount)
      }));

      console.log('✅ Normalized category breakdown:', normalizedCategories);
      return normalizedCategories;
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        console.log('🛑 Category breakdown fetch aborted');
        throw error;
      }
      console.error('❌ Error fetching category breakdown:', error);
      throw error;
    }
  },

  /**
   * Get comprehensive financial insights
   * @param {AbortSignal} signal - Abort signal for cancellation
   * @param {string} dataView - Data view: 'personal', 'partner', or 'combined'
   * @returns {Promise<Object>} Comprehensive insights data
   */
  async getInsights(signal, dataView = 'combined') {
    try {
      console.log('🧠 Dashboard Service: Fetching financial insights...', { dataView });
      
      const config = {
        ...this.getAuthHeader(),
        signal,
        params: { dataView }
      };
      
      const response = await dashboardAxios.get(`/api/dashboard/insights`, config);
      
      console.log('✅ Dashboard Service: Insights received:', {
        hasData: !!response.data?.data,
        insightsCount: response.data?.data?.generatedInsights?.length || 0
      });
      
      return response.data?.data || {};
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        console.log('🛑 Insights fetch aborted');
        throw error;
      }
      console.error('❌ Error fetching insights:', error);
      throw error;
    }
  }
};

export default dashboardService; 